import { memo, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getJobCategoriesByTrainingTrack, getTrainingTrack } from '../_api';
import DetailEntry from '../layouts/DetailEntry';
import DetailLayout from '../layouts/DetailLayout';

/**
 * Epic Training Track search results view
 * @param {any} id - primary key to identify the training track
 * @returns Training Track search result details
 */
const DetailsView = memo(function TrainingTrackDetails({
	id,
	hideJobCategories = false,
	enableLinks = false,
}) {
	const [sShowJobCategories, setShowJobCategories] = useState(false);
	const [sTrainingTrack, setTrainingTrack] = useState(null);
	const [sJobCategories, setJobCategories] = useState([]);
	const [sErrorMessage, setErrorMessage] = useState(null);
	const history = useHistory();

	/**
	 * trigger API call to get training track details upon page render
	 */
	useEffect(() => {
		let active = true;

		async function fetchApi() {
			try {
				const selectedTrainingTrack = await getTrainingTrack(id);
				setTrainingTrack(selectedTrainingTrack);
			} catch {
				setErrorMessage(
					'Encountered error when trying to fetch information on Training Track ID=' +
						id
				);
			}
		}

		if (active) {
			fetchApi();
		}

		return () => {
			active = false;
		};
	}, [id]);

	/**
	 * Trigger API call to get job categories upon render
	 */
	useEffect(() => {
		let active = true;

		async function fetchApi() {
			try {
				const selectedTrainingTrackJobCategories =
					await getJobCategoriesByTrainingTrack(id);
				setJobCategories(selectedTrainingTrackJobCategories);
			} catch {
				setErrorMessage(
					'Encountered error when trying to fetch information on Training Track ID = "' +
						id +
						'"'
				);
			}
		}

		if (active) {
			fetchApi();
		}

		return () => {
			active = false;
		};
	}, [id]);

	/**
	 * Toggle job category section visibility
	 * @param {boolean} expand
	 */
	const onExpandJobCategoriesHandler = async expand => {
		setShowJobCategories(expand);
	};

	return (
		<>
			{sErrorMessage && (
				<Alert
					severity='error'
					variant='filled'
					onClose={() => {
						setErrorMessage(null);
					}}
				>
					{sErrorMessage}
				</Alert>
			)}
			{sTrainingTrack?.trainingTrackID && (
				<DetailLayout>
					<DetailEntry
						label={'ID'}
						value={sTrainingTrack?.trainingTrackID}
					/>
					<DetailEntry
						label={'Name'}
						value={
							enableLinks && sTrainingTrack?.name ? (
								<Link
									underline='always'
									onClick={() =>
										history.push(
											'/EpicUtilities/TrainingTrack/Search?id=' +
												sTrainingTrack.trainingTrackID
										)
									}
									sx={{
										cursor: 'pointer',
									}}
								>
									{sTrainingTrack?.name}
								</Link>
							) : (
								sTrainingTrack?.name
							)
						}
					/>
					<DetailEntry
						label={'State'}
						value={sTrainingTrack?.active ? 'active' : 'inactive'}
					/>
					{!hideJobCategories && (
						<>
							<Box
								display='flex'
								justifyContent='space-between'
								alignItems='center'
							>
								<DetailEntry
									label={'Associated Job Categories'}
									disableLoading
								/>

								{sJobCategories?.length > 0 ? (
									<IconButton
										onClick={() =>
											onExpandJobCategoriesHandler(
												!sShowJobCategories
											)
										}
									>
										<ExpandMoreIcon />
									</IconButton>
								) : (
									<DetailEntry
										sx={{ height: '40px', padding: '10px' }}
										label={''}
										value={
											<CircularProgress
												size={24}
												variant='indeterminate'
											/>
										}
										skipSeparator={true}
									/>
								)}
							</Box>
							{sShowJobCategories &&
								(sJobCategories.length > 0 ? (
									sJobCategories.map((jobCategory, index) => (
										<DetailEntry
											key={jobCategory.id}
											label={`${index + 1}.`}
											value={
												<a
													href={
														'/EpicUtilities/JobCategory/Search?id=' +
														jobCategory.jobCategoryID
													}
												>
													{jobCategory.name}
												</a>
											}
											skipSeparator
										/>
									))
								) : (
									<>
										{sJobCategories.map((_, index) => (
											<Skeleton
												key={index}
												sx={{ fontSize: '14px' }}
												animation='pulse'
												variant='text'
												width='50%'
											/>
										))}
									</>
								))}
						</>
					)}
				</DetailLayout>
			)}
		</>
	);
});

export default DetailsView;
